/**
 * Party frames in the WoW raid-frame style: a gold "Party" header over one tall box per member.
 * The box IS the health bar (class-coloured fill over a dark empty part, absorb overlay and damage
 * trail), with a round role badge and the white name at the top left, a small HP % at the bottom
 * right, a hairline resource bar along the bottom and up to four debuff icons inside the box.
 * A debuff the player can dispel lights the box edge in its dispel colour; members out of range
 * (40 yd) or out of line of sight (+castux) fade. Sits about a third of the way across the screen.
 *
 * Clicking selects the member; hovering reports it for mouse-over healing (Hud.frameHoverId).
 */
import type { ContentRegistry, DeepReadonly, DispelType, SimQueries, UnitId, World } from '@keystone/sim';
import { hudNow } from '../../game/prediction';
import { el, setClass, setText } from '../dom';
import { StatBar, resourceVar } from '../kit';
import { FrameAuraRow } from './auraRows';
import { roleSvg } from './glyphs';
import { FRAME_RANGE, PCT, dispelTypesFor, dispellableType, hpPercent, nameColor } from './model';

export interface PartyFramesUpdate {
  world: DeepReadonly<World>;
  content: ContentRegistry;
  queries: SimQueries | null;
  playerId: UnitId | null;
  selectedId: UnitId | null;
  hoverId: UnitId | null;
}

export interface PartyFramesOptions {
  select(id: UnitId): void;
  /** Mouse-over unit (null on leave). */
  hover(id: UnitId | null): void;
  /** Frames to build (default 4). */
  slots?: number;
}

const DISPEL_CLS: Readonly<Record<DispelType, string>> = { magic: 'dispel-magic', poison: 'dispel-poison', curse: 'dispel-curse', disease: 'dispel-disease', enrage: 'dispel-enrage' };
/** Range is re-checked this often (ms, real time). */
const RANGE_EVERY_MS = 120;

function absorbOf(u: DeepReadonly<World>['units'][string]): number {
  let s = 0;
  for (const a of u.auras) s += a.absorbRemaining ?? 0;
  return s;
}

class PartyRow {
  readonly root: HTMLElement;
  unitId: UnitId | null = null;
  private readonly role: HTMLElement;
  private readonly name: HTMLElement;
  /** +personas: "AFK" / "Away" / "Left" tag next to the name. */
  private readonly tag: HTMLElement;
  private away: string | null = null;
  private readonly pct: HTMLElement;
  private readonly hp: StatBar;
  private readonly res: StatBar;
  private readonly debuffs: FrameAuraRow;
  private shownId: UnitId | null = null;
  private resId: string | null = null;
  private pctShown = -1;
  private dispel: DispelType | null = null;
  private dead = false;
  oor = false;

  constructor(parent: HTMLElement, opts: PartyFramesOptions) {
    this.root = el('div', 'fp-row hk-hit empty', parent);
    // The health bar fills the whole box; everything else sits on top of it.
    this.hp = new StatBar(this.root, { text: 'none', color: '#888', extra: true, className: 'fp-hp' });
    const head = el('div', 'fp-head', this.root);
    this.role = el('div', 'fp-role', head);
    this.name = el('div', 'fp-name', head);
    this.tag = el('div', 'fp-tag', head);
    this.pct = el('div', 'fp-pct hk-num', this.root);
    this.res = new StatBar(this.root, { text: 'none', height: 'sm', ghost: false, className: 'fp-res' });
    this.debuffs = new FrameAuraRow(this.root, { kind: 'debuff', max: 4, size: 'xs', className: 'fp-debuffs' });
    this.root.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      if (this.unitId) opts.select(this.unitId);
    });
    this.root.addEventListener('mouseenter', () => opts.hover(this.unitId));
    this.root.addEventListener('mouseleave', () => opts.hover(null));
  }

  update(f: PartyFramesUpdate, id: UnitId | null, realNow: number, myDispel: ReadonlySet<DispelType>): void {
    const { world, content } = f;
    this.unitId = id;
    const u = id ? world.units[id] : undefined;
    const changed = (u?.id ?? null) !== this.shownId;
    this.shownId = u?.id ?? null;
    setClass(this.root, 'empty', !u);
    if (!u) return;
    if (changed) {
      setText(this.name, u.name);
      this.hp.setColor(nameColor(u, content));
      this.role.replaceChildren(roleSvg(u.role));
      setClass(this.root, 'me', u.id === f.playerId);
      const cls = u.classId ? content.class(u.classId) : undefined;
      this.resId = cls?.resources[0]?.id ?? (Object.keys(u.resources)[0] ?? null);
      this.res.setColor(resourceVar(this.resId));
    }
    setClass(this.root, 'selected', u.id === f.selectedId);
    setClass(this.root, 'hovered', u.id === f.hoverId);
    if (this.dead !== !u.alive) {
      this.dead = !u.alive;
      setClass(this.root, 'dead', this.dead);
    }
    // +personas: AFK / wandered off (dimmed, tagged) and left the group (greyed out, "Left").
    const away = u.departed ? 'left' : (u.presence ?? null);
    if (away !== this.away) {
      if (this.away) this.root.classList.remove(`away-${this.away}`);
      if (away) this.root.classList.add(`away-${away}`);
      this.away = away;
      this.tag.textContent = away === 'left' ? 'Left' : away === 'afk' ? 'AFK' : away === 'wandering' ? 'Away' : '';
      this.pctShown = NaN; // re-render the HP text below
    }
    const absorb = absorbOf(u);
    if (changed) this.hp.snap(u.hp, u.maxHp, realNow, absorb);
    else this.hp.update(u.hp, u.maxHp, realNow, absorb);
    const p = u.departed ? -3 : u.alive ? hpPercent(u.hp, u.maxHp) : -2;
    if (p !== this.pctShown) {
      this.pctShown = p;
      this.pct.textContent = p === -3 ? '' : p < 0 ? 'Dead' : p >= 100 ? '' : PCT[p]!;
    }
    const pool = this.resId ? u.resources[this.resId as keyof typeof u.resources] : undefined;
    setClass(this.res.root, 'hk-hidden', !pool);
    if (pool) this.res.update(pool.cur, pool.max, realNow);
    this.debuffs.update(world, content, u, f.playerId, hudNow(world), myDispel);
    const d = u.alive ? dispellableType(u, content, myDispel) : null;
    if (d !== this.dispel) {
      if (this.dispel) this.root.classList.remove('dispel', DISPEL_CLS[this.dispel]);
      if (d) this.root.classList.add('dispel', DISPEL_CLS[d]);
      this.dispel = d;
    }
  }
}

export class PartyFrames {
  readonly root: HTMLElement;
  private readonly rows: PartyRow[] = [];
  private lastRangeAt = -Infinity;
  /** +castux: line of sight to a member (walls between dim the frame too, like WoW's range fade). */
  inSight: ((id: UnitId) => boolean) | null = null;

  constructor(parent: HTMLElement, opts: PartyFramesOptions) {
    this.root = el('div', 'fp party-frames', parent);
    el('div', 'fp-title', this.root, 'Party');
    for (let i = 0; i < (opts.slots ?? 4); i++) this.rows.push(new PartyRow(this.root, opts));
  }

  /** Reused per-frame context (no allocation). */
  private readonly f: PartyFramesUpdate = { world: null as never, content: null as never, queries: null, playerId: null, selectedId: null, hoverId: null };

  update(
    world: DeepReadonly<World>,
    content: ContentRegistry,
    queries: SimQueries | null,
    playerId: UnitId | null,
    selectedId: UnitId | null,
    hoverId: UnitId | null,
    realNow: number,
  ): void {
    const f = this.f;
    f.world = world;
    f.content = content;
    f.queries = queries;
    f.playerId = playerId;
    f.selectedId = selectedId;
    f.hoverId = hoverId;
    const me = f.playerId ? world.units[f.playerId] : undefined;
    const myDispel = dispelTypesFor(me?.classId ? content.class(me.classId) : undefined);
    const checkRange = realNow - this.lastRangeAt >= RANGE_EVERY_MS;
    if (checkRange) this.lastRangeAt = realNow;
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i]!;
      const id = world.partyIds[i] ?? null;
      row.update(f, id, realNow, myDispel);
      if (checkRange) {
        let oor = false;
        if (id && me && id !== me.id && f.queries) {
          try {
            oor = f.queries.spatial.rangeBetween(world as World, me.id, id) > FRAME_RANGE || (!!this.inSight && !this.inSight(id)); // +castux: LoS
          } catch {
            oor = false;
          }
        }
        if (oor !== row.oor) {
          row.oor = oor;
          setClass(row.root, 'oor', oor);
        }
      }
    }
  }
}
